import React from "react";
import { cn } from "@/lib/utils";

/**
 * Pill filter/label. Two surfaces:
 *   onDark=false (default) — light surface (Privacy/Terms/light demos)
 *   onDark=true            — dark surface (all marketing pages)
 * `selected` toggles solid-fill state. If `href` or `to` is provided,
 * renders as a link; otherwise a <button>.
 */
export function Tag({
  children,
  selected = false,
  onClick,
  href,
  to,
  onDark = false,
  className,
  "data-testid": testId,
  ...rest
}) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[13px] font-medium transition-colors duration-fast ease-out";

  const light = selected
    ? "bg-frac-purple text-frac-white border border-frac-purple"
    : "bg-transparent text-frac-purple border border-frac-gray-30 hover:bg-frac-purple-10";

  const dark = selected
    ? "bg-frac-violet text-frac-white border border-frac-violet"
    : "bg-transparent text-ink-primary border border-hairline hover:bg-[rgba(114,0,219,0.08)] hover:border-active-border";

  const classes = cn(base, onDark ? dark : light, className);

  if (to) {
    // Local anchor for hash links — keep it as <a> for now so Tag stays independent of router.
    return (
      <a href={to} className={classes} data-testid={testId} {...rest}>
        {children}
      </a>
    );
  }
  if (href) {
    return (
      <a href={href} className={classes} data-testid={testId} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={classes}
      data-testid={testId}
      {...rest}
    >
      {children}
    </button>
  );
}

export default Tag;
